import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { sportsProgramsContent } from '../content/educational/sportsProgramsContent';

const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 40;
    const footerHeight = 20;
    const lineSpacing = 3;
    const paragraphSpacing = 1.2;
    const bulletIndent = 12;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    // Helper function to process markdown text
    const processMarkdown = (text) => {
      let processedText = text;
      let segments = [];
      
      let lastIndex = 0;
      let boldRegex = /\*\*(.*?)\*\*/g;
      let match;
      
      while ((match = boldRegex.exec(processedText)) !== null) {
        if (match.index > lastIndex) {
          segments.push({
            text: processedText.substring(lastIndex, match.index),
            bold: false
          });
        }
        segments.push({
          text: match[1],
          bold: true
        });
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < processedText.length) {
        segments.push({
          text: processedText.substring(lastIndex),
          bold: false
        });
      }
      
      return segments;
    };

    // Helper function to handle bold keywords
    const processKeywords = (text, keywords) => {
      let processedText = text;
      keywords.forEach(keyword => {
        const regex = new RegExp(`(${keyword})`, 'gi');
        processedText = processedText.replace(regex, '**$1**');
      });
      return processedText;
    };

    // Helper function to handle bullet points and numbered lists
    const processList = (text, yPosition) => {
      const lines = text.split('\n');
      let currentY = yPosition;
      const bulletIndent = 12;
      const numberWidth = 10;
      const nestedIndent = 8;
      
      const renderTextWithBold = (text, x, y) => {
        const segments = processMarkdown(text);
        let currentX = x;
        
        segments.forEach(segment => {
          doc.setFont('helvetica', segment.bold ? 'bold' : 'normal');
          doc.text(segment.text, currentX, y);
          currentX += doc.getTextWidth(segment.text);
        });
        doc.setFont('helvetica', 'normal');
      };

      lines.forEach(line => {
        const isNested = line.startsWith('   ') || line.startsWith('\t');
        const baseIndent = isNested ? bulletIndent + nestedIndent : bulletIndent;
        const trimmedLine = line.trim();

        if (trimmedLine.startsWith('-')) {
          const bulletText = trimmedLine.substring(1).trim();
          doc.text('•', margin + baseIndent, currentY);
          const processedBullet = processKeywords(bulletText, keywords);
          const bulletLines = doc.splitTextToSize(processedBullet, contentWidth - (baseIndent + 12));
          
          bulletLines.forEach((bLine) => {
            if (currentY + 4 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            renderTextWithBold(bLine, margin + baseIndent + 8, currentY);
            currentY += 4;
          });
          currentY += 2;
        } else if (trimmedLine.match(/^\d+[\.)]/)) {
          const numberMatch = trimmedLine.match(/^(\d+[\.)]\s*)/);
          const numberPart = numberMatch[1];
          const textPart = trimmedLine.substring(numberMatch[0].length).trim();
          const processedNumber = processKeywords(textPart, keywords);
          const numberLines = doc.splitTextToSize(processedNumber, contentWidth - (baseIndent + numberWidth + 12));
          
          numberLines.forEach((nLine, index) => {
            if (currentY + 4 > pageHeight - footerHeight) {
              doc.addPage();
              addHeader();
              addFooter(doc.internal.getNumberOfPages());
              currentY = headerHeight + 12;
            }
            
            if (index === 0) {
              doc.text(numberPart, margin + baseIndent, currentY);
              renderTextWithBold(nLine, margin + baseIndent + numberWidth + 4, currentY);
            } else {
              renderTextWithBold(nLine, margin + baseIndent + numberWidth + 4, currentY);
            }
            currentY += 4;
          });
          currentY += 2;
        } else {
          const processedLine = processKeywords(trimmedLine, keywords);
          const textLines = doc.splitTextToSize(processedLine, contentWidth);
          
          textLines.forEach((tLine) => {
            renderTextWithBold(tLine, margin, currentY);
            currentY += 4;
          });
          currentY += 1;
        }
      });
      
      return currentY;
    };

    // Add the same header, footer, CTA, and content functions
    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    const addContent = () => {
      let yPosition = headerHeight + 12;
      let pageNumber = 1;

      // Add title with optimized styling
      doc.setFontSize(22);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition + 6, { align: 'center' });
      yPosition += (titleLines.length * 7) + 12;

      content.forEach((section, index) => {
        if (yPosition + 35 > pageHeight - footerHeight - 45) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 12;
        }

        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 6;

        // Process section content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(31, 41, 55);
        
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          yPosition = processList(paragraph, yPosition);
          yPosition += paragraphSpacing;
        });

        if (index === content.length - 1) {
          addCallToAction(pageHeight - footerHeight - 50);
        }
      });
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    // Add metadata
    doc.setProperties({
      title: title,
      subject: 'Sports Programs Guide',
      keywords: keywords.join(', '),
      author: 'Cause & Impact',
      creator: 'Cause & Impact'
    });

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateSportsProgramsOverview = async () => {
  return generatePDF(
    sportsProgramsContent.sections,
    sportsProgramsContent.title,
    sportsProgramsContent.keywords
  );
}; 